import React, { useState, useRef } from 'react';
import type { AdminFlowQuestion } from '../types';
import { INPUT, CHIP } from '../../styles/tokens';

interface QuestionEditModalProps {
    question: AdminFlowQuestion;
    onConfirm: (updated: AdminFlowQuestion) => void;
    onCancel: () => void;
}

// 從 token 組合出含 padding/font-size 的完整 class 字串
const INP = `text-sm rounded px-2 py-1.5 ${INPUT.base}`;
const INP_ANSWER = `text-sm rounded px-2 py-1.5 ${INPUT.answer}`;
const INP_LABEL = `text-sm rounded px-2 py-1.5 ${INPUT.label}`;
const CHIP_ANSWER = CHIP.answer;
const CHIP_LABEL = CHIP.label;

const QuestionEditModal: React.FC<QuestionEditModalProps> = ({ question, onConfirm, onCancel }) => {
    const [q, setQ] = useState<AdminFlowQuestion>({ ...question, options: question.options ? [...question.options] : [] });
    const [previewAnswer, setPreviewAnswer] = useState<string | boolean>('');
    const promptRef = useRef<HTMLTextAreaElement>(null);
    const highlightRef = useRef<HTMLDivElement>(null);

    const set = (patch: Partial<AdminFlowQuestion>) => setQ(prev => ({ ...prev, ...patch }));

    const isBoolean = q.type === 'boolean';
    const promptStr = typeof q.prompt === 'string' ? q.prompt : '';
    const promptTrue = isBoolean && typeof q.prompt === 'object' ? (q.prompt as { true: string; false: string; }).true : '';
    const promptFalse = isBoolean && typeof q.prompt === 'object' ? (q.prompt as { true: string; false: string; }).false : '';

    const handleTypeChange = (t: AdminFlowQuestion['type']) => {
        set({
            type: t,
            prompt: t === 'boolean' ? { true: '', false: '' } : (typeof q.prompt === 'string' ? q.prompt : ''),
            options: t === 'select' ? (q.options ?? []) : [],
            defaultValue: undefined,
            placeholder: undefined,
        });
        setPreviewAnswer('');
    };

    const addOption = () => set({ options: [...(q.options ?? []), { value: '', label: '', prompt: '' }] });

    type OptionItem = NonNullable<AdminFlowQuestion['options']>[0];
    const updateOption = (i: number, patch: Partial<OptionItem>) => {
        const opts = [...(q.options ?? [])];
        if ('label' in patch) {
            const newLabel = patch.label ?? '';
            const isDuplicate = opts.some((o, idx) => idx !== i && o.label === newLabel && newLabel !== '');
            if (isDuplicate) return;
            patch = { ...patch, value: newLabel };
        }
        opts[i] = { ...opts[i], ...patch };
        set({ options: opts });
    };

    const removeOption = (i: number) => {
        const opts = [...(q.options ?? [])];
        opts.splice(i, 1);
        set({ options: opts });
    };

    const insertPlaceholder = (placeholder: string) => {
        const el = promptRef.current;
        if (!el) return;
        const start = el.selectionStart ?? promptStr.length;
        const end = el.selectionEnd ?? start;
        const next = promptStr.slice(0, start) + placeholder + promptStr.slice(end);
        set({ prompt: next });
        requestAnimationFrame(() => {
            el.focus();
            el.setSelectionRange(start + placeholder.length, start + placeholder.length);
        });
    };

    // 把模板字串切成節點，::answer:: 與 ::label:: 用色票包起來
    const renderPromptTemplate = (text: string) => {
        if (!text) return null;
        const parts = text.split(/(::answer::|::label::)/g);
        return parts.map((part, i) => {
            if (part === '::answer::') return <span key={i} className={CHIP_ANSWER}>::answer::</span>;
            if (part === '::label::') return <span key={i} className={CHIP_LABEL}>::label::</span>;
            return <React.Fragment key={i}>{part}</React.Fragment>;
        });
    };

    // 預覽答案（select 型態下的當前選項）
    const selectedOpt = q.type === 'select'
        ? (q.options ?? []).find(o => o.value === previewAnswer)
        : undefined;
    const previewAns: string = isBoolean
        ? ''
        : q.type === 'select'
            ? (selectedOpt?.prompt ?? (typeof previewAnswer === 'string' ? previewAnswer : ''))
            : (typeof previewAnswer === 'string' ? previewAnswer : String(previewAnswer));
    const previewLabel: string = selectedOpt?.label ?? '＿＿＿';

    // 渲染替換後的預覽，並以色票標示被替換的片段
    const renderPreviewPrompt = () => {
        if (isBoolean) {
            const boolVal = previewAnswer === true || previewAnswer === 'true';
            const txt = boolVal ? promptTrue : promptFalse;
            return txt || null;
        }
        if (!promptStr) return null;
        const ansDisplay = previewAns || '＿＿＿';
        const parts = promptStr.split(/(::answer::|::label::)/g);
        return parts.map((part, i) => {
            if (part === '::answer::') return <span key={i} className={CHIP_ANSWER}>{ansDisplay}</span>;
            if (part === '::label::') return <span key={i} className={CHIP_LABEL}>{previewLabel}</span>;
            return <React.Fragment key={i}>{part}</React.Fragment>;
        });
    };

    const hasContent = isBoolean
        ? !!(previewAnswer === true ? promptTrue : promptFalse)
        : !!promptStr;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3.5 border-b dark:border-gray-700">
                    <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">編輯題目</h3>
                    <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-lg leading-none">✕</button>
                </div>

                {/* Body: 左右兩欄 */}
                <div className="flex-1 overflow-hidden flex">
                    {/* ── 左欄：表單 ── */}
                    <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 border-r dark:border-gray-700">
                        {/* ID & Type */}
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">ID</label>
                                <input value={q.id} onChange={e => set({ id: e.target.value })} placeholder="question-id" className={`w-full ${INP}`} />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">類型</label>
                                <select value={q.type} onChange={e => handleTypeChange(e.target.value as AdminFlowQuestion['type'])} className={`w-full ${INP}`}>
                                    <option value="text">text</option>
                                    <option value="select">select</option>
                                    <option value="image">image</option>
                                    <option value="boolean">boolean</option>
                                </select>
                            </div>
                        </div>

                        {/* Title & Required */}
                        <div className="flex gap-3 items-end">
                            <div className="flex-1">
                                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">標題</label>
                                <input value={q.title} onChange={e => set({ title: e.target.value })} className={`w-full ${INP}`} />
                            </div>
                            <label className="flex items-center gap-1.5 cursor-pointer text-sm text-gray-700 dark:text-gray-300 pb-1.5">
                                <input type="checkbox" checked={q.required} onChange={e => set({ required: e.target.checked })} className="accent-blue-600" />
                                必填
                            </label>
                            {q.type === 'boolean' && (
                                <label className="flex items-center gap-1.5 cursor-pointer text-sm text-gray-700 dark:text-gray-300 pb-1.5">
                                    <input type="checkbox"
                                        checked={q.defaultValue === true}
                                        onChange={e => set({ defaultValue: e.target.checked ? true : false })}
                                        className="accent-emerald-600" />
                                    預設勾選
                                </label>
                            )}
                        </div>

                        {/* Description（給 AI 配對參考，前端不顯示） */}
                        <div>
                            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                                題目補充說明
                                <span className="ml-1 text-gray-400 dark:text-gray-500">（給 AI 配對參考，使用者看不到）</span>
                            </label>
                            <textarea
                                rows={2}
                                value={q.description ?? ''}
                                onChange={e => set({ description: e.target.value })}
                                placeholder="例如：此題用於填入活動的舉辦地點，若資料含『地點』、『場地』、『地址』等欄位皆可採用。"
                                className={`w-full ${INP} resize-y`}
                            />
                        </div>

                        {/* Placeholder (text only) */}
                        {q.type === 'text' && (
                            <div>
                                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Placeholder（使用者輸入框提示灰字，可換行）</label>
                                <textarea rows={3} value={q.placeholder ?? ''} onChange={e => set({ placeholder: e.target.value })}
                                    placeholder="例如：請輸入活動名稱..." className={`w-full ${INP} resize-y`} />
                            </div>
                        )}

                        {/* Upload limits (image only) */}
                        {q.type === 'image' && (
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                                        最少上傳張數{q.required ? <span className="text-amber-500 ml-1">（必填時 ≥ 1）</span> : ''}
                                    </label>
                                    <input
                                        type="number" min={q.required ? 1 : 0} max={5}
                                        value={q.minUpload ?? (q.required ? 1 : 0)}
                                        onChange={e => {
                                            const v = Math.min(5, Math.max(q.required ? 1 : 0, parseInt(e.target.value) || 0));
                                            const maxU = q.maxUpload ?? 1;
                                            set({ minUpload: v, maxUpload: Math.max(maxU, v) });
                                        }}
                                        className={`w-full ${INP}`}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">最多上傳張數</label>
                                    <input
                                        type="number" min={q.minUpload ?? (q.required ? 1 : 0)} max={5}
                                        value={q.maxUpload ?? 1}
                                        onChange={e => {
                                            const v = Math.min(5, Math.max(q.minUpload ?? (q.required ? 1 : 0), parseInt(e.target.value) || 1));
                                            set({ maxUpload: v });
                                        }}
                                        className={`w-full ${INP}`}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Prompt */}
                        {isBoolean ? (
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Prompt (true)</label>
                                    <textarea rows={3} value={promptTrue} onChange={e => set({ prompt: { true: e.target.value, false: promptFalse } })}
                                        className={`w-full ${INP} resize-y`} />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Prompt (false)</label>
                                    <textarea rows={3} value={promptFalse} onChange={e => set({ prompt: { true: promptTrue, false: e.target.value } })}
                                        className={`w-full ${INP} resize-y`} />
                                </div>
                            </div>
                        ) : (
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <label className="text-xs text-gray-500 dark:text-gray-400">Prompt（預留字插入游標位置）</label>
                                    <div className="flex items-center gap-1">
                                        <button type="button" onClick={() => insertPlaceholder('::answer::')}
                                            className="text-[10px] px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border border-blue-300 dark:border-blue-700/60 hover:bg-blue-200 dark:hover:bg-blue-900/60 transition-colors font-mono">
                                            + ::answer::
                                        </button>
                                        {q.type === 'select' && (
                                            <button type="button" onClick={() => insertPlaceholder('::label::')}
                                                className="text-[10px] px-2 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700/60 hover:bg-emerald-200 dark:hover:bg-emerald-900/60 transition-colors font-mono">
                                                + ::label::
                                            </button>
                                        )}
                                    </div>
                                </div>
                                {/* Prompt 編輯區：透明文字 textarea 疊在語法高亮層上，讓 ::answer:: / ::label:: 顯眼 */}
                                <div className="relative">
                                    <div
                                        aria-hidden
                                        ref={highlightRef}
                                        className="absolute inset-0 text-sm font-mono border border-transparent rounded px-2 py-1.5 whitespace-pre-wrap break-words pointer-events-none overflow-hidden text-gray-700 dark:text-gray-100 leading-5"
                                    >
                                        {renderPromptTemplate(promptStr)}
                                        {'​'}
                                    </div>
                                    <textarea
                                        ref={promptRef}
                                        rows={3}
                                        value={promptStr}
                                        onChange={e => set({ prompt: e.target.value })}
                                        onScroll={e => {
                                            if (highlightRef.current) highlightRef.current.scrollTop = (e.target as HTMLTextAreaElement).scrollTop;
                                        }}
                                        spellCheck={false}
                                        className="w-full resize-y relative font-mono leading-5 text-sm border dark:border-gray-600 rounded px-2 py-1.5 bg-transparent text-transparent caret-gray-800 dark:caret-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Templates (text only) */}
                        {q.type === 'text' && (
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400">文字範本（供使用者快速套用）</label>
                                    <button
                                        type="button"
                                        onClick={() => set({ templates: [...(q.templates ?? []), ''] })}
                                        className="text-xs text-blue-500 hover:text-blue-700">+ 新增範本</button>
                                </div>
                                <div className="space-y-1.5">
                                    {(q.templates ?? []).map((tpl, ti) => (
                                        <div key={ti} className="flex gap-2 items-start">
                                            <textarea
                                                value={tpl}
                                                onChange={e => {
                                                    const next = [...(q.templates ?? [])];
                                                    next[ti] = e.target.value;
                                                    set({ templates: next });
                                                }}
                                                placeholder={`範本 ${ti + 1}`}
                                                rows={3}
                                                className={`flex-1 text-xs resize-y ${INP}`}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const next = [...(q.templates ?? [])];
                                                    next.splice(ti, 1);
                                                    set({ templates: next });
                                                }}
                                                className="text-red-400 hover:text-red-600 text-sm shrink-0 mt-1">✕</button>
                                        </div>
                                    ))}
                                    {(q.templates ?? []).length === 0 && (
                                        <p className="text-xs text-gray-400 dark:text-gray-500 py-2 text-center">尚無範本，點上方按鈕新增</p>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Options (select) */}
                        {q.type === 'select' && (
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400">選項</label>
                                    <button onClick={addOption} className="text-xs text-blue-500 hover:text-blue-700">+ 新增選項</button>
                                </div>
                                {(q.options ?? []).length > 0 && (
                                    <div className="mb-2 flex items-center gap-2">
                                        <label className="text-xs text-gray-500 dark:text-gray-400 shrink-0">預設選項</label>
                                        <select value={typeof q.defaultValue === 'string' ? q.defaultValue : ''}
                                            onChange={e => set({ defaultValue: e.target.value || undefined })}
                                            className={`flex-1 text-xs ${INP}`}>
                                            <option value="">（無預設）</option>
                                            {(q.options ?? []).map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.label || opt.value}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                                <div className="space-y-2">
                                    {(q.options ?? []).map((opt, oi) => (
                                        <div key={oi} className="border dark:border-gray-600 rounded p-2.5 space-y-1.5 bg-gray-50 dark:bg-gray-700/60">
                                            <div className="flex gap-2 items-center">
                                                <div className="flex-1 flex items-center gap-1">
                                                    <span className={`text-[10px] font-mono px-1 rounded ${CHIP_LABEL}`} title="此欄位將填入模板中的 ::label::">::label::</span>
                                                    <input placeholder="label（顯示名稱）" value={opt.label ?? ''} onChange={e => updateOption(oi, { label: e.target.value })}
                                                        className={`flex-1 text-xs ${INP_LABEL}`} />
                                                </div>
                                                <span className={`flex-1 text-xs px-2 py-1.5 rounded border dark:border-gray-600 bg-gray-50 dark:bg-gray-600 text-gray-400 dark:text-gray-500 select-none truncate`} title={opt.value || '（自動與名稱相同）'}>
                                                    {opt.value || <span className="italic">（自動）</span>}
                                                </span>
                                                <button onClick={() => removeOption(oi)} className="text-red-400 hover:text-red-600 text-sm shrink-0">✕</button>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <span className={`text-[10px] font-mono px-1 rounded ${CHIP_ANSWER}`} title="此欄位將填入模板中的 ::answer::">::answer::</span>
                                                <input placeholder="隱藏 prompt（選擇此選項時注入生成指令）" value={opt.prompt ?? ''}
                                                    onChange={e => updateOption(oi, { prompt: e.target.value })}
                                                    className={`flex-1 text-xs ${INP_ANSWER}`} />
                                            </div>
                                        </div>
                                    ))}
                                    {(q.options ?? []).length === 0 && (
                                        <p className="text-xs text-gray-400 dark:text-gray-500 py-2 text-center">尚無選項，點上方按鈕新增</p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ── 右欄：即時 Prompt 預覽 ── */}
                    <div className="w-64 shrink-0 flex flex-col px-4 py-4 space-y-3 overflow-y-auto bg-gray-50 dark:bg-gray-900/40">
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400">模擬使用者輸入</p>

                        {q.type === 'text' && (
                            <div className="flex items-center gap-1">
                                <span className={`text-[10px] font-mono px-1 rounded shrink-0 ${CHIP_ANSWER}`} title="輸入內容將填入 ::answer::">::answer::</span>
                                <input
                                    className={`flex-1 text-xs ${INP_ANSWER}`}
                                    placeholder={q.placeholder || '輸入範例答案…'}
                                    value={typeof previewAnswer === 'string' ? previewAnswer : ''}
                                    onChange={e => setPreviewAnswer(e.target.value)}
                                />
                            </div>
                        )}
                        {q.type === 'select' && (
                            <div className="flex flex-wrap gap-1.5">
                                {(q.options ?? []).length === 0 && (
                                    <p className="text-xs text-gray-400 dark:text-gray-500">尚無選項</p>
                                )}
                                {(q.options ?? []).map(opt => {
                                    const active = previewAnswer === opt.value;
                                    return (
                                        <button key={opt.value} type="button"
                                            onClick={() => setPreviewAnswer(opt.value)}
                                            title={`label = ${opt.label || opt.value}`}
                                            className={`px-2 py-1 rounded text-xs border transition-colors ${active
                                                ? 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-200 border-emerald-400 dark:border-emerald-600 ring-1 ring-emerald-400'
                                                : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-emerald-400'}`}>
                                            {opt.label || opt.value}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                        {q.type === 'select' && selectedOpt && (
                            <div className="flex items-start gap-1 text-[11px]">
                                <span className={`font-mono px-1 rounded shrink-0 mt-0.5 ${CHIP_ANSWER}`}>::answer::</span>
                                <span className="flex-1 text-gray-600 dark:text-gray-300 break-words italic">
                                    {selectedOpt.prompt || <span className="text-gray-400">（此選項未設定 prompt）</span>}
                                </span>
                            </div>
                        )}
                        {q.type === 'boolean' && (
                            <label className="flex items-center gap-2 cursor-pointer text-xs text-gray-700 dark:text-gray-300">
                                <input type="checkbox"
                                    checked={previewAnswer === true || previewAnswer === 'true'}
                                    onChange={e => setPreviewAnswer(e.target.checked)}
                                    className="accent-blue-600" />
                                模擬勾選
                            </label>
                        )}
                        {q.type === 'image' && (
                            <p className="text-xs text-gray-400 dark:text-gray-500 italic">（圖片上傳類型，以 [圖片] 示意）</p>
                        )}

                        <div className="border-t dark:border-gray-700 pt-3">
                            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Prompt 預覽</p>
                            <pre className="text-[11px] whitespace-pre-wrap break-words bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded p-2 min-h-[60px] font-mono leading-relaxed">
                                {hasContent
                                    ? renderPreviewPrompt()
                                    : <span className="text-gray-400 dark:text-gray-600 not-italic">（尚未設定 Prompt）</span>}
                            </pre>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-5 py-3.5 border-t dark:border-gray-700">
                    <button onClick={onCancel} className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">取消</button>
                    <button onClick={() => onConfirm(q)}
                        className="text-sm bg-blue-600 text-white px-4 py-1.5 rounded-lg hover:bg-blue-700 transition-colors font-medium">
                        確認
                    </button>
                </div>
            </div>
        </div>
    );
};

export default QuestionEditModal;
